
import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { format, subHours, subDays , parse,parseISO } from 'date-fns';
import { fromZonedTime  } from 'date-fns-tz';


dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone


export default function generateInsights(data) {
  const now = dayjs().tz(TIMEZONE);
  const oneDayAgo = now.subtract(24, 'hour');
  const sevenDaysAgo = now.subtract(7, 'day');

  const hourlyCount = {};
  const dailyCount = {};
  const heatmapRaw = [];
  const linkOpenHourly = {};
  const cities24h = {};
  const cities7d = {};

  const platform24h = {};
  const platform7d = {};


  const linkSentVsOpened = {};

 let totalSent = 0;
  let totalOpened = 0;
  let sent24h = 0;
  let opened24h = 0;
  let sent7d = 0;
  let opened7d = 0;

  data.forEach((item) => {
    const respondedAt = dayjs(item.responded_at).utc().tz(TIMEZONE);
    const linkOpenedAt = item.link_open_at ? dayjs(item.link_open_at).utc().tz(TIMEZONE) : null;

    // Hourly (last 24 hrs)
    if (respondedAt.isAfter(oneDayAgo)) {
      const hour = respondedAt.startOf('hour').format('YYYY-MM-DD HH:00:00');
      hourlyCount[hour] = (hourlyCount[hour] || 0) + 1;
      totalSent++;
      sent24h++;

      if (!linkSentVsOpened[hour]) {
        linkSentVsOpened[hour] = { sent: 0, opened: 0 };
      }
      linkSentVsOpened[hour].sent++;

      // If this DM has link_open_at, consider it opened
      if (linkOpenedAt) {
        linkSentVsOpened[hour].opened++;
        totalOpened++;
        opened24h++;
      }
    }

    // Daily (last 7 days)
    if (respondedAt.isAfter(sevenDaysAgo)) {
      const day = respondedAt.format('YYYY-MM-DD');
      dailyCount[day] = (dailyCount[day] || 0) + 1;
      sent7d++;
      if (linkOpenedAt) {
        opened7d++;
      }
    }


    // Heatmap (7d × 24h)
    if (respondedAt.isAfter(sevenDaysAgo)) {
      const day = respondedAt.format('YYYY-MM-DD');
      const hour = respondedAt.hour().toString().padStart(2, '0');
      heatmapRaw.push({ day, hour, value: 1 });
    }

    // Link opened - hourly
    if (linkOpenedAt && linkOpenedAt.isAfter(oneDayAgo)) {
      const hour = linkOpenedAt.startOf('hour').format('YYYY-MM-DD HH:00:00');
      linkOpenHourly[hour] = (linkOpenHourly[hour] || 0) + 1;
    }

    // Cities 24h
    if (linkOpenedAt && linkOpenedAt.isAfter(oneDayAgo)) {
      const city = item.city;
      if (city) cities24h[city] = (cities24h[city] || 0) + 1;
    }

    // Cities 7d
    if (linkOpenedAt && linkOpenedAt.isAfter(sevenDaysAgo)) {
      const city = item.city;
      if (city) cities7d[city] = (cities7d[city] || 0) + 1;
    }

    // Platform 24h
    if (respondedAt.isAfter(oneDayAgo)) {
      const platform = item.platform || 'Unknown';
      platform24h[platform] = (platform24h[platform] || 0) + 1;
    }

    // Platform 7d
    if (respondedAt.isAfter(sevenDaysAgo)) {
      const platform = item.platform || 'Unknown';
      platform7d[platform] = (platform7d[platform] || 0) + 1;
    }


  });

  // Fill empty slots for last 24 hours
  const hourlyArray = [];
  const linkSentOpenedArray = [];
  for (let i = 0; i < 24; i++) {
    const hour = now.subtract(i, 'hour').startOf('hour');
    const key = hour.format('YYYY-MM-DD HH:00:00');
    hourlyArray.push({
      datetime: hour.format('YYYY-MM-DD HH:mm'),
      dm_sent: hourlyCount[key] || 0,
    });

    linkSentOpenedArray.push({
      datetime: hour.format('YYYY-MM-DD HH:mm'),
      sent: linkSentVsOpened[key]?.sent || 0,
      opened: linkSentVsOpened[key]?.opened || 0,
    });
  }


  // Fill empty slots for last 7 days
  const dailyArray = [];
  for (let i = 0; i < 7; i++) {
    const day = now.subtract(i, 'day');
    const key = day.format('YYYY-MM-DD');
    dailyArray.push({
      date: key,
      dm_sent: dailyCount[key] || 0,
    });
  }

  // Fill empty link open hourly
  const linkOpenHourlyArray = [];
  for (let i = 0; i < 24; i++) {
    const hour = now.subtract(i, 'hour').startOf('hour');
    const key = hour.format('YYYY-MM-DD HH:00:00');
    linkOpenHourlyArray.push({
      datetime: hour.format('YYYY-MM-DD HH:mm'),
      dm_sent: linkOpenHourly[key] || 0,
    });
  }

  // Generate complete heatmap with zeros
  const heatmapData = [];
  for (let i = 0; i < 7; i++) {
    const day = now.subtract(i, 'day');
    const dayLabel = day.format('YYYY-MM-DD');
    for (let h = 0; h < 24; h++) {
      const hourLabel = h.toString().padStart(2, '0');
      const count = heatmapRaw.filter(e => e.day === dayLabel && e.hour === hourLabel).length;
      heatmapData.push({ day: dayLabel, hour: hourLabel, value: count });
    }
  }


    // Convert city counts to array format
const cities24hArray = Object.entries(cities24h)
  .map(([city, count]) => ({ city, count }))
  .sort((a, b) => b.count - a.count);

const cities7dArray = Object.entries(cities7d)
  .map(([city, count]) => ({ city, count }))
  .sort((a, b) => b.count - a.count);



  const platform24hArray = Object.entries(platform24h)
  .map(([platform, count]) => ({ platform, count }))
  .sort((a, b) => b.count - a.count);

  const platform7dArray = Object.entries(platform7d)
  .map(([platform, count]) => ({ platform, count }))
  .sort((a, b) => b.count - a.count);


  const overallCTR = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
  const ctr24h = sent24h > 0 ? (opened24h / sent24h) * 100 : 0;
  const ctr7d = sent7d > 0 ? (opened7d / sent7d) * 100 : 0;


  return {
    hourlyCount: hourlyArray.reverse(),
    dailyCount: dailyArray.reverse(),
    heatmapMatrix: heatmapData.reverse(),
    linkOpenHourly: linkOpenHourlyArray.reverse(),
    cities24h: cities24hArray,
    cities7d: cities7dArray,
    linkSentVsOpened: linkSentOpenedArray.reverse(),
    overallCTR: parseFloat(overallCTR.toFixed(2)),
    ctr24h: parseFloat(ctr24h.toFixed(2)),
    ctr7d: parseFloat(ctr7d.toFixed(2)),
    sent24h,
    opened24h,
    sent7d,
    opened7d,
    platform24hour : platform24hArray,
    platform7day  : platform7dArray,
  };
}
